/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Spinner, Button } from 'reactstrap';
import apiOnboarding from '@src/services/api-onboarding';
import { useAuthOnboarding } from '@src/utility/hooks/useAuthOnboarding';
import { AuditTable } from './AuditTable';
import { AuditPagination } from './AuditPagination';
import { FilterAudit } from './Filter';

export interface AuditAction {
  id: string;
  userName: string;
  email: string;
  systemOrigin: string;
  functionality: string;
  actionPath: string;
  actionType: 'Criação' | 'Atualização' | 'Deleção';
  createAt: string;
  oldValues: any;
  newValues: any;
}

interface AuditLog {
  id: number;
  event: string;
  auditable_type: string;
  url: string;
  user_details: {
    name: string;
    email: string;
    systemOrigin: string;
  } | null;
  old_values: any;
  new_values: any;
  created_at: string;
}

interface AuditResponse {
  data: AuditLog[];
  current_page: number;
  total: number;
}

const systemOrigin = process.env.REACT_APP_SYSTEM_ORIGIN;
const url = `/api/audit/logs?systemOrigin=${systemOrigin}`;

export const AuditScreen: React.FC = () => {
  const [originalData, setOriginalData] = useState<AuditAction[]>([]); // Dados originais da API
  const [filteredData, setFilteredData] = useState<AuditAction[]>([]); // Dados filtrados localmente
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isShowFilter, setIsShowFilter] = useState(false);
  const [filterData, setFilterData] = useState<Partial<AuditAction>>({});
  const { verifyLogin } = useAuthOnboarding();
  const itemsPerPage = 5;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await verifyLogin();

      const response = await apiOnboarding.get<AuditResponse>(url);
      // Transformar os dados para o formato AuditAction
      const mappedData = response.data.data.map(
        (log: AuditLog): AuditAction => {
          const logEvent: any =
            // eslint-disable-next-line no-nested-ternary
            log.event === 'created'
              ? 'Criação'
              : log.event === 'updated'
              ? 'Atualização'
              : 'Deleção';

          return {
            id: log.id.toString(),
            userName: log.user_details?.name || 'Desconhecido',
            email: log.user_details?.email || 'Desconhecido',
            systemOrigin: log.user_details?.systemOrigin || 'Desconhecido',
            functionality: log.auditable_type.split('\\').pop() || 'Indefinido',
            actionPath: log.url,
            actionType: logEvent,
            oldValues: log.old_values,
            newValues: log.new_values,
            createAt: log.created_at,
          };
        }
      );

      setOriginalData(mappedData);
      setFilteredData(mappedData);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [verifyLogin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyFilters = (data: AuditAction[], filters: Partial<AuditAction>) => {
    return data.filter((item: any) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        if (key === 'createAt') {
          return new Date(item[key]).toISOString().startsWith(value as string);
        }
        return String(item[key])
          .toLowerCase()
          .includes(String(value).toLowerCase());
      });
    });
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleFilterSubmit = (formValues: Partial<AuditAction>) => {
    setFilterData(formValues);
    const filtered = applyFilters(originalData, formValues);
    setFilteredData(filtered);
    setCurrentPage(1);
    setIsShowFilter(false);
  };

  const handleClearFilter = () => {
    setFilterData({});
    setFilteredData(originalData); // Restaura os dados originais
    setCurrentPage(1);
    setIsShowFilter(false);
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h1 className="mb-4">Auditoria de Ações</h1>
        </Col>
        <Col className="text-right">
          <Button color="primary" onClick={() => setIsShowFilter(true)}>
            Filtrar
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          {loading ? (
            <div className="text-center">
              <Spinner color="primary" />
            </div>
          ) : (
            <AuditTable data={currentItems} />
          )}
        </Col>
      </Row>
      <Row className="justify-content-end" style={{ marginTop: 16 }}>
        <AuditPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </Row>
      <FilterAudit
        filterData={filterData}
        isShowFilter={isShowFilter}
        onClearFilterData={handleClearFilter}
        onSubmitFilter={handleFilterSubmit}
        toggleShowFilter={() => setIsShowFilter(!isShowFilter)}
      />
    </Container>
  );
};

export default AuditScreen;
